import { readonly, ref, useAttrs, watch } from "vue";

/**
 * The text state of a text control: the live value, the default a form reset restores, and the
 * flags that decide whether the attribute may move the text and whether the length constraints
 * apply.
 *
 * `value` is deliberately not a declared prop. Vue installs a non-configurable accessor on the host
 * for every declared prop, which cannot be replaced by the live `value` accessor a text control has
 * to expose, so the attribute is read through `attrs` instead.
 *
 * The two channels normalize separately because the controls disagree about what an absent value
 * is: `sp-textarea` is a string throughout, while `sp-text-field` keeps `null` for "no default".
 *
 * Observable state is readonly: every transition runs through the actions below, so a direct write
 * would leave the dirty and user-edit flags behind.
 *
 * @param {Object} [options]
 * @param {(value: *) => *} [options.normalizeText] - applied by `setText` and `setUserText`. A
 *   default reaching the text through adoption or `reset` carries `normalizeDefault` instead.
 * @param {(value: *) => *} [options.normalizeDefault] - applied to every write of the default
 * @param {() => *} [options.seedDefault] - the raw default when the `value` attribute is absent,
 *   read once at setup and normalized like any other. Defaults to reading the absent attribute.
 * @returns {{
 *   text: Readonly<import('vue').Ref<*>>,
 *   defaultValue: Readonly<import('vue').Ref<*>>,
 *   isUserEdited: Readonly<import('vue').Ref<Boolean>>,
 *   hasUserEdits: Readonly<import('vue').Ref<Boolean>>,
 *   setText: (value: *) => void,
 *   setUserText: (value: *) => void,
 *   setDefaultValue: (value: *) => void,
 *   reset: () => void,
 * }}
 */
export function useTextModel({ normalizeText = identity, normalizeDefault = identity, seedDefault } = {}) {
  const attrs = useAttrs();

  const defaultValue = ref(seedValue());
  const text = ref(defaultValue.value);

  // The dirty value flag of the HTML forms model: the value has been set, by a user edit or by a
  // write to the `value` property. Only a form reset clears it. While it is false the field
  // follows its default.
  const isDirty = ref(false);

  // Whether that last change came from the user. The length constraints read this: the platform
  // fails `tooLong`/`tooShort` only for a value the user edited.
  const isUserEdited = ref(false);

  // Whether the user has ever edited the field, which a programmatic write does not undo.
  const hasUserEdits = ref(false);

  // The `value` content attribute names the default, so it moves the text only while the field is
  // clean — it never sets the dirty flag itself.
  watch(() => attrs.value, setDefaultValue);

  function seedValue() {
    if (attrs.value !== undefined && attrs.value !== null) {
      return normalizeDefault(attrs.value);
    }

    return normalizeDefault(seedDefault ? seedDefault() : attrs.value);
  }

  function setText(value) {
    text.value = normalizeText(value);
    isDirty.value = true;
    isUserEdited.value = false;
  }

  function setUserText(value) {
    text.value = normalizeText(value);
    isDirty.value = true;
    isUserEdited.value = true;
    hasUserEdits.value = true;
  }

  function setDefaultValue(value) {
    defaultValue.value = normalizeDefault(value);

    if (!isDirty.value) {
      text.value = defaultValue.value;
    }
  }

  function reset() {
    text.value = defaultValue.value;
    isDirty.value = false;
    isUserEdited.value = false;
    hasUserEdits.value = false;
  }

  return {
    text: readonly(text),
    defaultValue: readonly(defaultValue),
    isUserEdited: readonly(isUserEdited),
    hasUserEdits: readonly(hasUserEdits),
    setText,
    setUserText,
    setDefaultValue,
    reset,
  };
}

function identity(value) {
  return value;
}
